import { jwtDecode } from "jwt-decode";
import React, { useEffect, useState } from 'react';
import { Col, Image, Row, Spinner, Stack } from 'react-bootstrap';
import { MdVerified, MdWarning } from 'react-icons/md';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import LOGIN_BANNER from "../../assets/images/login-banner.svg";
import LOGO_WHITE from '../../assets/images/logo-white.svg';
import { isExpiredTime } from '../../helpers/utils';
import { accountEmailVerificationAPI, loginAsVerificationAPI } from '../../services/auth.service';
import { redirectionRoutes } from "../../routes/redirectionRoutes";
import storage from "../../helpers/storage";
import { useCustomStore } from "../../redux/store";

const LoginAsComponent: React.FC = () => {
    const params = useParams();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const AppName = process.env.REACT_APP_NAME;
    const [loading, setLoading] = useState(true);
    const [expired, setExpired] = useState(false);
    const { setUser, setAuth0Login } = useCustomStore();
    const navigate = useNavigate();   
    
    /**
     * component mounted hook
     */
    useEffect(() => {
        // Clear local Storage for user to login
        storage.clearToken();
        
        if (token) {
            verify();
        }else{
            setExpired(true);
            setLoading(false);
        }
        // Define cleanup logic here
        return () => {
            setExpired(false);
        };
    }, [searchParams]);

    /**
     * On open link verify the account with link
     * @async
     * @param {token: string} values
     * @returns {*}
     */
    const verify = async () => {
        setLoading(true);
        loginAsVerificationAPI({ token: token }).then((response: any) => {
            handleLoginResponse(response);
        }).catch((error: any) => {
            setLoading(false);
            setExpired(true);
            toast(error.data.message, { type: toast.TYPE.ERROR });
        })
    }

    /**
     * Handles the response after a successful login.
     * @param {Object} response - The response data from the login API.
     * @returns {void}
     */
    const handleLoginResponse = (response: any) => {
        setLoading(false);
        storage.setToken(response.data.token.access.token);
        storage.setRefreshToken(response.data.token.refresh.token);
        setUser(response.data?.user);
        if(!response.data?.user?.profileVerification?.status) {
            navigate(redirectionRoutes.profile.path, { replace: true });
        } else {
            navigate(redirectionRoutes.dashboard.path, { replace: true });
        }
    }

    return (
        <Row className="g-0 vh-100">
            <Col
                md={6}
            >
                <Stack className='custom-primary-gradient text-center justify-content-around h-100 p-4' gap={4}>
                    <div className='user-select-none'>
                        <Image
                            fluid={true}
                            src={LOGO_WHITE}
                            alt={`${AppName} Logo`}
                            width={240}
                            height={80}
                        />
                    </div>
                    <div>
                        <Image
                            fluid={true}
                            src={LOGIN_BANNER}
                            alt={`${AppName}`}
                            width={500}
                            height={334}
                        />
                    </div>
                    <div className='lh-sm text-white'>
                        <div className='fw-light fs-4'>AI-Powered</div>
                        <div className='fw-semibold fs-1 text-uppercase'>Penetration Testing</div>
                        <div className='fw-light fs-4'>for Enterprises</div>
                    </div>
                </Stack>
            </Col>
            <Col md={6} className="col-md-6 d-flex flex-column p-3">
                <div className="custom-width-320 d-flex flex-column flex-grow-1 mw-100 mx-auto py-3">
                    <div className='my-auto py-4'>
                        {
                            loading && <>
                                <p className="mb-1 opacity-75 text-uppercase">Account verification</p>
                                <h1 className="fw-semibold mb-4 pb-1 fs-2">{AppName}</h1>
                                <p className='mb-4 opacity-75 pb-1 d-inline-flex align-items-center gap-2'>
                                    <Spinner animation="border" variant="primary" size='sm'>
                                        <span className="visually-hidden">Account Verifying...</span>
                                    </Spinner> Verifying your account, please wait...
                                </p>
                            </>
                        }
                        {!loading &&
                            <>
                                {expired ?
                                    <>
                                        <div className='mb-3 display-4 text-custom-danger' aria-label='Link Expired'><MdWarning /></div>
                                        <p className="mb-1 opacity-75 text-uppercase">Account verification</p>
                                        <h1 className="fw-semibold mb-4 pb-1 fs-2">{AppName}</h1>
                                        <p className='mb-4 opacity-75 pb-1'>
                                            This token has expired. Please contact the admin for assistance.
                                        </p>
                                    </> :
                                    <>
                                        <div className='mb-3 display-4 text-custom-success' aria-label='verification Success'><MdVerified /></div>
                                        <p className="mb-1 opacity-75 text-uppercase">Account verification</p>
                                        <h1 className="fw-semibold mb-4 pb-1 fs-2">{AppName}</h1>
                                        <p className='mb-4 opacity-75 pb-1'>
                                            Your account has been successfully verified.
                                        </p>
                                    </>
                                }
                            </>
                        }
                    </div>
                </div>
            </Col>
        </Row>
    )
}
export default LoginAsComponent;